import API from './API';
import { ElMessageBox, ElMessage, ElLoading } from 'element-plus';

/*
 * Il database non ha foreign key (tranne operazioni -> impianto), quindi il
 * "cascade" va orchestrato da qui: si cancella sempre dalle foglie verso la
 * radice usando le DELETE già esposte dal backend per ogni entità.
 * I fogli di lavoro NON vengono mai toccati: restano come documenti storici.
 */

const del = (risorsa, id) => API().delete(`/${risorsa}/${id}`);

const deleteMany = async (risorsa, items) => {
  for (const item of items || []) {
    await del(risorsa, item.id);
  }
};

const deleteModulo = (modulo) => del('modulo', modulo.id);

const deleteInverter = async (inverter) => {
  await deleteMany('modulo', inverter.moduli);
  await del('inverter', inverter.id);
};

const deleteSezione = async (sezione) => {
  for (const inverter of sezione.inverters || []) {
    await deleteInverter(inverter);
  }
  await del('sezione', sezione.id);
};

const deleteImpianto = async (impianto) => {
  // le operazioni hanno una FK reale verso impianto: vanno tolte prima
  await deleteMany('operazioni', impianto.operazioni);
  for (const sezione of impianto.sezioni || []) {
    await deleteSezione(sezione);
  }
  await del('impianto', impianto.id);
};

const deleteCentrale = async (centrale) => {
  for (const impianto of centrale.impianti || []) {
    await deleteImpianto(impianto);
  }
  await deleteMany('ref-tec', centrale.referenteTecnico);
  await del('centrale', centrale.id);
};

const deleteContratto = async (contratto) => {
  for (const centrale of contratto.centrali || []) {
    await deleteCentrale(centrale);
  }
  await del('contratto', contratto.id);
};

const deleteProprietario = async (proprietario) => {
  for (const contratto of proprietario.contratti || []) {
    await deleteContratto(contratto);
  }
  await deleteMany('ref-amm', proprietario.referenteAmministrativo);
  await del('proprietario', proprietario.id);
};

/*
 * Conteggio del sottoalbero, per mostrare nel dialog di conferma
 * esattamente cosa verrà cancellato.
 */

const nuovoConteggio = () => ({
  contratti: 0,
  centrali: 0,
  impianti: 0,
  sezioni: 0,
  inverter: 0,
  moduli: 0,
  'referenti amministrativi': 0,
  'referenti tecnici': 0,
  operazioni: 0,
});

const contaInverter = (inverter, tot) => {
  tot.inverter++;
  tot.moduli += (inverter.moduli || []).length;
};

const contaSezione = (sezione, tot) => {
  tot.sezioni++;
  (sezione.inverters || []).forEach((i) => contaInverter(i, tot));
};

const contaImpianto = (impianto, tot) => {
  tot.impianti++;
  tot.operazioni += (impianto.operazioni || []).length;
  (impianto.sezioni || []).forEach((s) => contaSezione(s, tot));
};

const contaCentrale = (centrale, tot) => {
  tot.centrali++;
  tot['referenti tecnici'] += (centrale.referenteTecnico || []).length;
  (centrale.impianti || []).forEach((i) => contaImpianto(i, tot));
};

const contaContratto = (contratto, tot) => {
  tot.contratti++;
  (contratto.centrali || []).forEach((c) => contaCentrale(c, tot));
};

const contaProprietario = (proprietario, tot) => {
  tot['referenti amministrativi'] += (
    proprietario.referenteAmministrativo || []
  ).length;
  (proprietario.contratti || []).forEach((c) => contaContratto(c, tot));
};

const riepilogo = (tot) => {
  const parti = Object.entries(tot)
    .filter(([, n]) => n > 0)
    .map(([nome, n]) => `${n} ${nome}`);
  return parti.length > 0 ? parti.join(', ') : '';
};

/*
 * Dialog di conferma + esecuzione con overlay di caricamento.
 */

const conferma = async (messaggio) => {
  try {
    await ElMessageBox.confirm(messaggio, 'Conferma eliminazione', {
      confirmButtonText: 'Elimina definitivamente',
      cancelButtonText: 'Annulla',
      confirmButtonClass: 'el-button--danger',
      type: 'warning',
    });
    return true;
  } catch {
    return false;
  }
};

const esegui = async (azione, nomeEntita) => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Eliminazione in corso…',
  });
  try {
    await azione();
    ElMessage.success(`${nomeEntita} eliminato correttamente`);
    return true;
  } catch (e) {
    console.error(e);
    ElMessage.error(
      'Eliminazione interrotta: alcuni elementi potrebbero non essere stati ' +
        'cancellati. Ricarica la pagina e riprova.'
    );
    return false;
  } finally {
    loading.close();
  }
};

const messaggioConRiepilogo = (intro, dettaglio) =>
  dettaglio
    ? `${intro} Verranno eliminati anche: ${dettaglio}. L'operazione non è reversibile.`
    : `${intro} L'operazione non è reversibile.`;

/*
 * Funzioni usate dai componenti: dialog di conferma + cascade.
 * Ritornano true se l'eliminazione è avvenuta (il chiamante poi ricarica).
 */

export default {
  async eliminaProprietario(proprietario, denominazione) {
    const tot = nuovoConteggio();
    contaProprietario(proprietario, tot);
    const dettaglio = riepilogo(tot);
    try {
      await ElMessageBox.prompt(
        `Stai per eliminare il cliente "${denominazione}" e tutti i suoi dati` +
          (dettaglio ? ` (${dettaglio})` : '') +
          '. I fogli di lavoro NON verranno eliminati e resteranno come ' +
          'storico. L\'operazione non è reversibile.\n' +
          'Per confermare digita il nome del cliente:',
        'Conferma eliminazione cliente',
        {
          confirmButtonText: 'Elimina definitivamente',
          cancelButtonText: 'Annulla',
          confirmButtonClass: 'el-button--danger',
          type: 'warning',
          inputValidator: (valore) =>
            (valore || '').trim().toLowerCase() ===
              denominazione.trim().toLowerCase() ||
            'Il nome inserito non corrisponde',
        }
      );
    } catch {
      return false;
    }
    return esegui(() => deleteProprietario(proprietario), 'Cliente');
  },

  async eliminaContratto(contratto) {
    const tot = nuovoConteggio();
    (contratto.centrali || []).forEach((c) => contaCentrale(c, tot));
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare il contratto "${contratto.nome || contratto.id}".`,
          riepilogo(tot)
        )
      ))
    )
      return false;
    return esegui(() => deleteContratto(contratto), 'Contratto');
  },

  async eliminaCentrale(centrale) {
    const tot = nuovoConteggio();
    (centrale.impianti || []).forEach((i) => contaImpianto(i, tot));
    tot['referenti tecnici'] = (centrale.referenteTecnico || []).length;
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare la centrale "${
            centrale.nomeCentrale || centrale.id
          }".`,
          riepilogo(tot)
        )
      ))
    )
      return false;
    return esegui(() => deleteCentrale(centrale), 'Centrale');
  },

  async eliminaImpianto(impianto) {
    const tot = nuovoConteggio();
    (impianto.sezioni || []).forEach((s) => contaSezione(s, tot));
    tot.operazioni = (impianto.operazioni || []).length;
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare l'impianto "${
            impianto.nomeImpianto || impianto.id
          }".`,
          riepilogo(tot)
        )
      ))
    )
      return false;
    return esegui(() => deleteImpianto(impianto), 'Impianto');
  },

  async eliminaSezione(sezione) {
    const tot = nuovoConteggio();
    (sezione.inverters || []).forEach((i) => contaInverter(i, tot));
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare la sezione "${sezione.nome || sezione.id}".`,
          riepilogo(tot)
        )
      ))
    )
      return false;
    return esegui(() => deleteSezione(sezione), 'Sezione');
  },

  async eliminaInverter(inverter) {
    const tot = nuovoConteggio();
    tot.moduli = (inverter.moduli || []).length;
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare l'inverter "${inverter.nome || inverter.id}".`,
          riepilogo(tot)
        )
      ))
    )
      return false;
    return esegui(() => deleteInverter(inverter), 'Inverter');
  },

  async eliminaModulo(modulo) {
    const nome = [modulo.marca, modulo.modello].filter(Boolean).join(' ');
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare il modulo "${nome || modulo.id}".`,
          ''
        )
      ))
    )
      return false;
    return esegui(() => deleteModulo(modulo), 'Modulo');
  },

  async eliminaReferente(referente) {
    const nome = [referente.nome, referente.cognome].filter(Boolean).join(' ');
    if (
      !(await conferma(
        messaggioConRiepilogo(
          `Stai per eliminare il referente "${nome || referente.id}".`,
          ''
        )
      ))
    )
      return false;
    return esegui(() => del('ref-amm', referente.id), 'Referente');
  },
};
